/**
 * pricingEngine.js
 * Moteur de calcul de fourchettes de prix – E2I Solutions
 *
 * IMPORTANT : Ces fourchettes sont indicatives et ne constituent pas un devis ferme.
 * Ajustez les valeurs en fonction de votre politique tarifaire réelle.
 */

// ─── Données de configuration ────────────────────────────────────────────────

export const PRESTATIONS = [
  {
    id: 'conception-carte',
    label: 'Conception carte électronique',
    description: 'Schématique, routage PCB, validation design',
    icon: 'circuit',
  },
  {
    id: 'systeme-embarque',
    label: 'Système embarqué',
    description: 'Architecture matérielle complète, sélection composants',
    icon: 'chip',
  },
  {
    id: 'iot',
    label: 'IoT / Connecté',
    description: 'Capteurs, protocoles sans fil, passerelle cloud',
    icon: 'wifi',
  },
  {
    id: 'firmware',
    label: 'Firmware / Logiciel embarqué',
    description: 'Développement logiciel bas niveau, RTOS, drivers',
    icon: 'code',
  },
  {
    id: 'tests-validation',
    label: 'Tests & Validation',
    description: 'Protocoles de test, qualification, conformité',
    icon: 'beaker',
  },
  {
    id: 'integration-systeme',
    label: 'Intégration système',
    description: 'Assemblage, intégration HW/SW, mise en service',
    icon: 'cog',
  },
]

export const COMPLEXITES = [
  {
    id: 'faible',
    label: 'Faible',
    description: 'Projet simple, périmètre bien défini',
    badge: 'Entrée de gamme',
  },
  {
    id: 'moyenne',
    label: 'Moyenne',
    description: 'Quelques interfaces, contraintes modérées',
    badge: 'Standard',
  },
  {
    id: 'elevee',
    label: 'Élevée',
    description: 'Multiples interfaces, contraintes techniques fortes',
    badge: 'Avancé',
  },
  {
    id: 'tres-elevee',
    label: 'Très élevée',
    description: 'Haute fréquence, certification, sécurité fonctionnelle',
    badge: 'Expert',
  },
]

export const VOLUMES = [
  {
    id: 'unitaire',
    label: 'Unitaire / Prototype',
    description: 'Un exemplaire, phase de R&D',
    badge: 'Prototype',
  },
  {
    id: 'petite-serie',
    label: 'Petite série',
    description: '2 à 50 unités',
    badge: '2 – 50 unités',
  },
  {
    id: 'moyenne-serie',
    label: 'Moyenne série',
    description: '51 unités et plus',
    badge: '51+ unités',
  },
]

export const URGENCES = [
  {
    id: 'standard',
    label: 'Standard',
    description: 'Délai normal de production',
    badge: null,
  },
  {
    id: 'rapide',
    label: 'Rapide',
    description: 'Livraison accélérée souhaitée',
    badge: '+urgence',
  },
  {
    id: 'tres-urgent',
    label: 'Très urgent',
    description: 'Délai critique, mobilisation prioritaire',
    badge: 'Prioritaire',
  },
]

export const OPTIONS = [
  {
    id: 'documentation',
    label: 'Documentation technique',
    description: 'Dossier de définition, schémas annotés, rapport de validation',
  },
  {
    id: 'suivi-production',
    label: 'Suivi de production',
    description: 'Accompagnement et supervision lors de la phase de fabrication série',
  },
  {
    id: 'maintenance',
    label: 'Maintenance & support',
    description: 'Contrat de maintenance préventive et corrective post-livraison',
  },
  {
    id: 'cahier-charges',
    label: 'Co-construire le cahier des charges',
    description: 'Atelier collaboratif pour définir ensemble les spécifications et exigences du projet',
  },
]

// ─── Tables de prix (€, fourchette min-max) ──────────────────────────────────

const BASE_PRICES = {
  'conception-carte':  { min: 2500,  max: 6500  },
  'systeme-embarque':  { min: 5000,  max: 14000 },
  'iot':               { min: 3500,  max: 11000 },
  'firmware':          { min: 1800,  max: 7000  },
  'tests-validation':  { min: 900,   max: 3500  },
  'integration-systeme': { min: 2500, max: 9000 },
}

const COMPLEXITY_MULTIPLIERS = {
  'faible':      { min: 0.55, max: 0.75 },
  'moyenne':     { min: 0.85, max: 1.10 },
  'elevee':      { min: 1.25, max: 1.65 },
  'tres-elevee': { min: 1.80, max: 2.50 },
}

const VOLUME_MULTIPLIERS = {
  'unitaire':      { min: 1.00, max: 1.00 },
  'petite-serie':  { min: 0.88, max: 0.92 },
  'moyenne-serie': { min: 0.72, max: 0.80 },
}

const URGENCY_MULTIPLIERS = {
  'standard':    { min: 1.00, max: 1.00 },
  'rapide':      { min: 1.15, max: 1.28 },
  'tres-urgent': { min: 1.40, max: 1.60 },
}

const OPTIONS_ADDITIONS = {
  'documentation':    { min: 600,  max: 1500 },
  'suivi-production': { min: 900,  max: 2200 },
  'maintenance':      { min: 1200, max: 3000 },
}

// ─── Fonctions utilitaires ────────────────────────────────────────────────────

/** Arrondit à la centaine la plus proche (lisibilité) */
function roundToHundred(value) {
  return Math.round(value / 100) * 100
}

/**
 * Calcule la fourchette de prix pour une configuration donnée.
 * @param {Object} config - { prestation, complexite, volume, urgence, options }
 * @returns {{ min: number, max: number, breakdown: Object }}
 */
export function calculateEstimate(config) {
  const { prestation, complexite, volume, urgence, options = [] } = config

  if (!prestation || !complexite || !volume || !urgence) {
    return null
  }

  const base     = BASE_PRICES[prestation]
  const compMult = COMPLEXITY_MULTIPLIERS[complexite]
  const volMult  = VOLUME_MULTIPLIERS[volume]
  const urgMult  = URGENCY_MULTIPLIERS[urgence]

  if (!base || !compMult || !volMult || !urgMult) return null

  // Calcul progressif : base × complexité × volume × urgence
  let min = base.min * compMult.min * volMult.min * urgMult.min
  let max = base.max * compMult.max * volMult.max * urgMult.max

  // Ajout des options (plate)
  const optionsBreakdown = {}
  for (const optionId of options) {
    const opt = OPTIONS_ADDITIONS[optionId]
    if (opt) {
      min += opt.min
      max += opt.max
      optionsBreakdown[optionId] = opt
    }
  }

  // Arrondi
  min = roundToHundred(min)
  max = roundToHundred(max)

  // Garantit que min < max avec au moins 10 % d'écart
  if (max <= min) max = roundToHundred(min * 1.15)

  return {
    min,
    max,
    formatted: {
      min: formatPrice(min),
      max: formatPrice(max),
    },
    breakdown: {
      base,
      complexity: compMult,
      volume: volMult,
      urgency: urgMult,
      options: optionsBreakdown,
    },
  }
}

/** Formate un nombre en euros (ex: 3 500 €) */
export function formatPrice(amount) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount)
}

/** Renvoie le libellé d'une prestation à partir de son id */
export function getPrestationLabel(id) {
  return PRESTATIONS.find((p) => p.id === id)?.label ?? id
}

export function getComplexiteLabel(id) {
  return COMPLEXITES.find((c) => c.id === id)?.label ?? id
}

export function getVolumeLabel(id) {
  return VOLUMES.find((v) => v.id === id)?.label ?? id
}

export function getUrgenceLabel(id) {
  return URGENCES.find((u) => u.id === id)?.label ?? id
}

export function getOptionLabel(id) {
  return OPTIONS.find((o) => o.id === id)?.label ?? id
}
